import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { OnboardingService } from '../../services/onboardingService';
import { ANALYTICS_EVENTS, trackEvent } from '../../analytics';
import { reportActivationEvent } from '../../services/authService';
import styles from './Onboarding.module.css';
import { useAuth } from '../../context/AuthContext';
import { ONBOARDING_ACTIONS, ONBOARDING_SKIP_REASONS, ONBOARDING_STEPS } from '@aluvo/constants/src/onboardingDiagnostics';
import { coinEligibleTitles, titleKey } from '@aluvo/constants/src/onboardingTitles';
import { OnboardingDiagnostics, storedUserId } from '../../diagnostics/onboardingDiagnostics';
import { clearOnboardingDraft, loadOnboardingDraft, saveOnboardingDraft } from '../../utils/onboardingDraft';
import { onboardingExitPath } from './OnboardingVerify';

// Screen step (1-3) → onboarding step key used by diagnostics.
const STEP_KEYS = { 1: ONBOARDING_STEPS.WELCOME.key, 2: ONBOARDING_STEPS.TITLES.key, 3: ONBOARDING_STEPS.GENRES.key };
const SUBMIT_ERROR_MESSAGE = "We couldn't save your picks. Check your connection and try again.";

// ─── Step 1: Welcome ──────────────────────────────────────────────────────────
function StepWelcome({ onNext }) {
    return (
        <div className={styles.stepWelcome}>
            <div className={styles.welcomeIcon}>🪙</div>
            <h1 className={styles.welcomeHeadline}>Let's build your collection</h1>
            <p className={styles.welcomeSubtext}>
                Tell us what you've already watched and earn your first coin in under a minute.
            </p>
            <button className={styles.ctaBtn} onClick={onNext}>
                Get started
            </button>
        </div>
    );
}

// ─── Step 2: Title Selection ──────────────────────────────────────────────────
// Optional: Continue with any number of picks, or Skip for now with none.
const keyOf = (item) => titleKey(item.kind, item.tmdbId ?? item.id);

function StepTitles({ selectedIds, onToggle, onNext, onSkip, onLoadError }) {
    const [titles, setTitles]     = useState([]);
    const [loading, setLoading]   = useState(true);
    const [page, setPage]         = useState(1);
    const [hasMore, setHasMore]   = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [query, setQuery]       = useState('');
    const [searching, setSearching] = useState(false);
    // The title list could not be loaded at all (not "no results").
    const [loadError, setLoadError] = useState(false);
    const debounceRef = useRef(null);
    // Only the latest list request may update the list (a slow response must
    // never overwrite newer results).
    const requestRef = useRef(0);

    useEffect(() => () => clearTimeout(debounceRef.current), []);

    const parseResults = (data) =>
        data?.data?.results ?? data?.results ?? (Array.isArray(data) ? data : []);

    const loadPopular = useCallback((pageNum = 1, append = false) => {
        const ticket = append ? requestRef.current : ++requestRef.current;
        const current = () => ticket === requestRef.current;
        if (pageNum === 1) { setLoading(true); setLoadError(false); }
        else setLoadingMore(true);
        const startedAt = Date.now();
        OnboardingService.getPopularTitles(pageNum)
            .then(data => {
                if (!current()) return;
                const results = parseResults(data);
                setTitles(prev => append ? [...prev, ...results] : results);
                setHasMore(results.length > 0);
                setPage(pageNum);
            })
            .catch((error) => {
                // Page 1 failing leaves nothing to pick (Skip for now still works).
                onLoadError?.(error, { operation: 'load_titles', endpoint: 'onboarding_titles', blocking: pageNum === 1, durationMs: Date.now() - startedAt, page: pageNum });
                if (!current()) return;
                setHasMore(false);
                if (pageNum === 1) setLoadError(true);
            })
            .finally(() => {
                // A stale page-2 load must still release the "loading more" guard.
                if (append) setLoadingMore(false);
                if (current()) setLoading(false);
            });
    }, [onLoadError]);

    useEffect(() => { loadPopular(1); }, [loadPopular]);

    function handleSearch(e) {
        const q = e.target.value;
        setQuery(q);
        clearTimeout(debounceRef.current);
        setPage(1);
        setHasMore(true);
        if (!q.trim()) { setSearching(false); loadPopular(1); return; }
        setSearching(true);
        debounceRef.current = setTimeout(async () => {
            const ticket = ++requestRef.current;
            const current = () => ticket === requestRef.current;
            try {
                const data = await OnboardingService.searchTitles(q.trim(), 1);
                if (!current()) return;
                const results = parseResults(data);
                setTitles(results);
                setHasMore(results.length > 0);
                setLoadError(false);
                setLoading(false);
            } catch (error) {
                // The visible list stays as it was; the user can still pick from it.
                onLoadError?.(error, { operation: 'search_titles', endpoint: 'media_search', blocking: false });
                if (current()) setLoading(false);
            }
            finally { if (current()) setSearching(false); }
        }, 350);
    }

    async function handleLoadMore() {
        if (loadingMore || !hasMore || loading || searching) return;
        const nextPage = page + 1;
        if (query.trim()) {
            const ticket = requestRef.current;
            setLoadingMore(true);
            try {
                const data = await OnboardingService.searchTitles(query.trim(), nextPage);
                if (ticket !== requestRef.current) return;
                const results = parseResults(data);
                setTitles(prev => [...prev, ...results]);
                setHasMore(results.length > 0);
                setPage(nextPage);
            } catch (error) {
                onLoadError?.(error, { operation: 'search_titles', endpoint: 'media_search', blocking: false, page: nextPage });
                setHasMore(false);
            }
            finally { setLoadingMore(false); }
        } else {
            loadPopular(nextPage, true);
        }
    }

    const selectedCount = selectedIds.size;

    return (
        <div className={styles.stepTitles}>
            <h2 className={styles.stepHeadline}>Pick a few movies you've watched</h2>
            <p className={styles.stepSubtext}>
                Help us personalize your Aluvo experience. Choose a few favorites, or skip for now.
            </p>

            <div className={styles.searchRow}>
                <input
                    className={styles.searchInput}
                    type="text"
                    placeholder="Search movies & shows…"
                    value={query}
                    onChange={handleSearch}
                    autoComplete="off"
                />
                {selectedIds.size > 0 && (
                    <span className={styles.counter}>{selectedIds.size} selected</span>
                )}
            </div>

            {/* Never a disabled button: with picks it continues, without it skips. */}
            {selectedCount > 0 ? (
                <button className={styles.ctaBtn} onClick={onNext} style={{ marginBottom: '20px' }}>
                    {`Continue with ${selectedCount} title${selectedCount !== 1 ? 's' : ''}`}
                </button>
            ) : (
                <button type="button" className={`${styles.ctaBtn} ${styles.ctaBtnOutline}`} onClick={onSkip} style={{ marginBottom: '20px' }}>
                    Skip for now
                </button>
            )}

            {(loading || searching) ? (
                <div className={styles.gridLoading}>
                    {Array.from({ length: 12 }).map((_, i) => (
                        <div key={i} className={styles.posterSkeleton} />
                    ))}
                </div>
            ) : titles.length === 0 && loadError && !query.trim() ? (
                <div className={styles.emptyState}>
                    <p>We couldn't load titles. Check your connection and try again, or skip for now.</p>
                    <button type="button" className={styles.loadMoreBtn} onClick={() => loadPopular(1)}>Try again</button>
                </div>
            ) : titles.length === 0 ? (
                <p className={styles.emptyState}>No results found. Try a different search.</p>
            ) : (
                <>
                    <div className={styles.grid}>
                        {titles.map(title => {
                            const id = keyOf(title);
                            const isSelected = selectedIds.has(id);
                            return (
                                <button
                                    key={id}
                                    className={`${styles.posterBtn} ${isSelected ? styles.posterSelected : ''}`}
                                    onClick={() => onToggle(id, title)}
                                    aria-pressed={isSelected}
                                >
                                    {title.posterUrl ? (
                                        <img
                                            src={title.posterUrl}
                                            alt={title.title}
                                            className={styles.posterImg}
                                            loading="lazy"
                                        />
                                    ) : (
                                        <div className={styles.posterPlaceholder}>
                                            <span>{title.kind === 'tv' ? '📺' : '🎬'}</span>
                                        </div>
                                    )}
                                    {isSelected && (
                                        <div className={styles.checkOverlay}>
                                            <span className={styles.checkmark}>✓</span>
                                        </div>
                                    )}
                                    <div className={styles.posterLabel}>
                                        <span className={styles.posterTitle}>{title.title}</span>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                    {hasMore && (
                        <button
                            className={styles.loadMoreBtn}
                            onClick={handleLoadMore}
                            disabled={loadingMore}
                        >
                            {loadingMore ? 'Loading…' : 'Load more'}
                        </button>
                    )}
                </>
            )}
        </div>
    );
}

// ─── Step 3: Genre Preferences ────────────────────────────────────────────────
function StepGenres({ selectedGenreIds, onToggle, onFinish, onSkip, submitting, submitError }) {
    const [genres, setGenres] = useState([]);

    useEffect(() => {
        OnboardingService.getGenres()
            .then(data => setGenres(Array.isArray(data) ? data : (data?.genres ?? [])))
            .catch(() => setGenres([]));
    }, []);

    return (
        <div className={styles.stepGenres}>
            <h2 className={styles.stepHeadline}>What do you like watching?</h2>
            <p className={styles.stepSubtext}>
                We'll use this to personalise your Discover feed. Totally optional.
            </p>

            <div className={styles.genreGrid}>
                {genres.map(genre => {
                    const isSelected = selectedGenreIds.has(genre.id);
                    return (
                        <button
                            key={genre.id}
                            className={`${styles.genreChip} ${isSelected ? styles.genreChipSelected : ''}`}
                            onClick={() => onToggle(genre.id)}
                            aria-pressed={isSelected}
                        >
                            {genre.name}
                        </button>
                    );
                })}
            </div>

            <div className={styles.stepFooter}>
                {submitError && <p className={styles.submitError} role="alert">{submitError}</p>}
                <button className={styles.ctaBtn} onClick={onFinish} disabled={submitting}>
                    {submitting ? 'Setting up…' : 'Finish'}
                </button>
                <button className={styles.skipBtn} onClick={onSkip} disabled={submitting}>
                    Skip for now
                </button>
            </div>
        </div>
    );
}

// ─── Shell ────────────────────────────────────────────────────────────────────
export default function Onboarding() {
    const navigate = useNavigate();
    const { user } = useAuth();
    // On a reload this page renders before AuthContext restores the session.
    const userId = user?.id || user?._id || storedUserId();
    // Resume: the step and picks from the last time this user was here.
    const [draft] = useState(() => loadOnboardingDraft(userId));

    const [step, setStep]                         = useState(draft?.step ?? 1);
    const [selectedTitles, setSelectedTitles]     = useState(() => new Map((draft?.titles ?? []).map((t) => [t.id, t]))); // `kind:tmdbId` → { id, tmdbId, kind, title, posterUrl }
    const [selectedGenreIds, setSelectedGenreIds] = useState(() => new Set(draft?.genreIds ?? []));
    const [submitting, setSubmitting]             = useState(false);
    const [submitError, setSubmitError]           = useState(null);
    const startedRef = useRef(false);
    const submittingRef = useRef(false);
    const doneRef = useRef(false);

    useEffect(() => {
        if (startedRef.current) return;
        startedRef.current = true;
        trackEvent(ANALYTICS_EVENTS.ONBOARDING_STARTED, { platform: 'web' }, { onceKey: 'onboarding-started' });
        reportActivationEvent('onboarding_started');
        if (draft && draft.step > 1) OnboardingDiagnostics.resumed(STEP_KEYS[draft.step], { restoredStep: STEP_KEYS[draft.step] });
    }, [draft]);

    useEffect(() => {
        OnboardingDiagnostics.stepViewed(STEP_KEYS[step], step === 2 ? { selected_count: selectedTitles.size } : {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step]);

    useEffect(() => {
        if (doneRef.current) return;
        saveOnboardingDraft(userId, { step, titles: Array.from(selectedTitles.values()), genreIds: Array.from(selectedGenreIds) });
    }, [userId, step, selectedTitles, selectedGenreIds]);

    function completeStep(step, nextStep) {
        const counts = step === 'titles' ? { selected_count: selectedTitles.size } : {};
        OnboardingDiagnostics.stepAction(step, ONBOARDING_ACTIONS.CONTINUE, counts);
        trackEvent(ANALYTICS_EVENTS.ONBOARDING_STEP_COMPLETED, { platform: 'web', step, ...counts });
        reportActivationEvent('onboarding_step_completed', { step, ...counts });
        setStep(nextStep);
    }

    // "Skip for now" on titles: an intentional skip (never abandonment).
    function skipTitles() {
        const reason = ONBOARDING_SKIP_REASONS.USER_SKIPPED;
        OnboardingDiagnostics.stepAction('titles', ONBOARDING_ACTIONS.SKIP, { selected_count: 0, reason });
        trackEvent(ANALYTICS_EVENTS.ONBOARDING_SKIPPED, { platform: 'web', step: 'titles', reason });
        reportActivationEvent('onboarding_skipped', { step: 'titles', reason, selected_count: 0 });
        setStep(3);
    }

    const handleTitlesLoadError = useCallback((error, { operation, endpoint, blocking, durationMs, page }) => {
        OnboardingDiagnostics.stepError('titles', { operation, endpoint, error, blocking, durationMs, extra: page ? { attempt: page } : {} });
    }, []);

    function toggleTitle(id, item) {
        const selecting = !selectedTitles.has(id);
        OnboardingDiagnostics.titleToggled(selecting, { selectedCount: selectedTitles.size + (selecting ? 1 : -1), mediaType: item.kind });
        setSelectedTitles(prev => {
            const next = new Map(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.set(id, {
                    id,
                    tmdbId:    Number(item.tmdbId ?? item.id),
                    kind:      item.kind,
                    title:     item.title,
                    posterUrl: item.posterUrl ?? null,
                });
            }
            return next;
        });
    }

    function toggleGenre(id) {
        setSelectedGenreIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    }

    async function submit(genreIds = [], { skippedGenres = false } = {}) {
        // A second click before the button disables would otherwise send twice.
        if (submittingRef.current) return;
        submittingRef.current = true;
        setSubmitting(true);
        setSubmitError(null);
        const startedAt = Date.now();
        const watchedTitles = Array.from(selectedTitles.values());
        OnboardingDiagnostics.stepAction('genres', skippedGenres ? ONBOARDING_ACTIONS.SKIP : ONBOARDING_ACTIONS.CONTINUE, {
            selected_count: watchedTitles.length,
            genre_count: genreIds.length,
        });
        try {
            if (skippedGenres) {
                trackEvent(ANALYTICS_EVENTS.ONBOARDING_SKIPPED, { platform: 'web', step: 'genres' });
                reportActivationEvent('onboarding_skipped', { step: 'genres' });
            } else {
                trackEvent(ANALYTICS_EVENTS.ONBOARDING_STEP_COMPLETED, { platform: 'web', step: 'genres' });
            }
            // POST /onboarding persists onboarding_completed_at server-side.
            await OnboardingService.complete(watchedTitles, genreIds);
        } catch (err) {
            console.error('[Onboarding] submit failed:', err?.message);
            OnboardingDiagnostics.stepError('genres', {
                operation: 'submit_onboarding', endpoint: 'onboarding_submit', error: err, blocking: true, durationMs: Date.now() - startedAt,
            });
            submittingRef.current = false;
            setSubmitting(false);
            // Previously the button just re-enabled with no explanation.
            setSubmitError(SUBMIT_ERROR_MESSAGE);
            return;
        }
        trackEvent(ANALYTICS_EVENTS.ONBOARDING_COMPLETED, { platform: 'web', title_count: watchedTitles.length, genre_count: genreIds.length, titles_skipped: watchedTitles.length === 0 });
        doneRef.current = true;
        clearOnboardingDraft(userId);
        OnboardingDiagnostics.finished();
        try {
            const stored = JSON.parse(localStorage.getItem('aluvo_user') || 'null');
            if (stored) localStorage.setItem('aluvo_user', JSON.stringify({ ...stored, needsOnboarding: false, onboardingCompleted: true }));
        } catch { /* ignore */ }
        // The coin step offers the user's own picks; with none there is nothing
        // to offer, so it is bypassed (no empty page, no stand-in title).
        const eligible = coinEligibleTitles(watchedTitles);
        if (eligible.length) {
            navigate('/onboarding/verify', { state: { watchedTitles: eligible } });
            return;
        }
        OnboardingDiagnostics.stepAutoSkipped(ONBOARDING_STEPS.VERIFY.key, watchedTitles.length
            ? ONBOARDING_SKIP_REASONS.NO_ELIGIBLE_MOVIES
            : ONBOARDING_SKIP_REASONS.NO_SELECTED_MOVIES, { selected_count: watchedTitles.length });
        navigate(onboardingExitPath(), { replace: true });
    }

    const TOTAL_STEPS = 3;

    return (
        <div className={styles.page}>
            {step > 1 && (
                <div className={styles.progressWrap}>
                    <div
                        className={styles.progressBar}
                        style={{ width: `${((step - 1) / (TOTAL_STEPS - 1)) * 100}%` }}
                    />
                </div>
            )}

            <div className={styles.inner}>
                {step === 1 && <StepWelcome onNext={() => completeStep('welcome', 2)} />}
                {step === 2 && (
                    <StepTitles
                        selectedIds={new Set(selectedTitles.keys())}
                        onToggle={(id, item) => toggleTitle(id, item)}
                        onNext={() => completeStep('titles', 3)}
                        onSkip={skipTitles}
                        onLoadError={handleTitlesLoadError}
                    />
                )}
                {step === 3 && (
                    <StepGenres
                        selectedGenreIds={selectedGenreIds}
                        onToggle={toggleGenre}
                        onFinish={() => submit(Array.from(selectedGenreIds))}
                        onSkip={() => submit([], { skippedGenres: true })}
                        submitting={submitting}
                        submitError={submitError}
                    />
                )}
            </div>
        </div>
    );
}